import React from 'react';
import { businessInfo, services, faqs } from '../../data/mock';

// Enhanced Local Business Schema with Google Maps
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Plumber",
    "@id": "https://basinplumbing.com/#organization",
    "name": businessInfo.name,
    "alternateName": "Basin Plumbing Odessa",
    "url": "https://basinplumbing.com",
    "logo": "https://basinplumbing.com/images/basin-plumbing-logo.png",
    "image": "https://basinplumbing.com/images/basin-plumbing-logo.png",
    "description": "24/7 Emergency plumbing services in Odessa, Midland, and the Permian Basin. Expert water heater, drain cleaning, gas line, and trenchless plumbing services. Licensed and insured plumbers serving West Texas.",
    "telephone": businessInfo.phone,
    "email": businessInfo.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": businessInfo.address.street,
      "addressLocality": businessInfo.address.city,
      "addressRegion": businessInfo.address.state,
      "postalCode": businessInfo.address.zip,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "31.8673",
      "longitude": "-102.4252"
    },
    "hasMap": "https://www.google.com/maps/place/Basin+Plumbing/@31.8673,-102.4252,17z/data=!3m1!4b1!4m6!3m5!1s0x86fbc7c0baf766d5:0x9959fe1a1d46471!8m2!3d31.8673!4d-102.4252!16s%2Fg%2F11c5qh5_kj",
    "areaServed": businessInfo.serviceAreas.map(area => ({
      "@type": "City",
      "name": area,
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    })),
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "31.8673",
        "longitude": "-102.4252"
      },
      "geoRadius": "80000"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      }
    ],
    "priceRange": "$$",
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Financing Available"],
    "currenciesAccepted": "USD",
    "sameAs": [
      businessInfo.social.facebook,
      "https://www.google.com/maps/place/Basin+Plumbing/@31.8673,-102.4252,17z"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Plumbing Services",
      "itemListElement": services.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.shortDesc,
          "url": `https://basinplumbing.com/services/${service.slug}`,
          "provider": {
            "@type": "Plumber",
            "name": "Basin Plumbing"
          },
          "areaServed": {
            "@type": "State",
            "name": "Texas"
          }
        },
        "position": index + 1
      }))
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "247",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Robert M."
        },
        "datePublished": "2026-01-15",
        "reviewBody": "Basin Plumbing saved us during a water heater emergency on a Sunday morning. They arrived within an hour and had our new water heater installed by afternoon. Exceptional service!",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      }
    ],
    "slogan": "Your Trusted 24/7 Emergency Plumber in the Permian Basin",
    "knowsAbout": [
      "Plumbing",
      "Water Heater Installation",
      "Water Heater Repair",
      "Drain Cleaning",
      "Gas Line Repair",
      "Gas Line Installation",
      "Trenchless Plumbing",
      "Sewer Line Replacement",
      "Emergency Plumbing",
      "Residential Plumbing",
      "Commercial Plumbing"
    ],
    "makesOffer": services.map(service => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": service.title
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// FAQ Schema - Enhanced with @id reference for better schema linking
export const FAQSchema = ({ customFaqs = faqs, pageUrl = null }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageUrl ? { "@id": `${pageUrl}#faq` } : {}),
    "mainEntity": customFaqs.map((faq, index) => ({
      "@type": "Question",
      "name": faq.question,
      "position": index + 1,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Service Schema - Enhanced with more specific local SEO signals
export const ServiceSchema = ({ service }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://basinplumbing.com/services/${service.slug}#service`,
    "serviceType": service.title,
    "name": `${service.title} in Odessa & Midland TX`,
    "provider": {
      "@type": "Plumber",
      "@id": "https://basinplumbing.com/#organization",
      "name": businessInfo.name,
      "telephone": businessInfo.phone,
      "url": "https://basinplumbing.com",
      "logo": "https://basinplumbing.com/images/basin-plumbing-logo.png",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": businessInfo.address.street,
        "addressLocality": businessInfo.address.city,
        "addressRegion": businessInfo.address.state,
        "postalCode": businessInfo.address.zip,
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "31.8673",
        "longitude": "-102.4252"
      },
      "priceRange": "$$",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "247"
      }
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Odessa",
        "containedInPlace": { "@type": "State", "name": "Texas" }
      },
      {
        "@type": "City", 
        "name": "Midland",
        "containedInPlace": { "@type": "State", "name": "Texas" }
      },
      ...businessInfo.serviceAreas.filter(a => a !== 'Odessa' && a !== 'Midland').map(area => ({
        "@type": "City",
        "name": area,
        "containedInPlace": { "@type": "State", "name": "Texas" }
      }))
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "31.8673",
        "longitude": "-102.4252"
      },
      "geoRadius": "80000"
    },
    "description": service.description,
    "url": `https://basinplumbing.com/services/${service.slug}`,
    "image": service.image,
    "termsOfService": "https://basinplumbing.com/contact",
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "availableAtOrFrom": {
        "@type": "Place",
        "name": "Basin Plumbing",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Odessa",
          "addressRegion": "TX"
        }
      },
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${service.title} Services`,
      "itemListElement": service.features.map((feature, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": feature
        },
        "position": index + 1
      }))
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Article Schema for Blog Posts (BlogPosting) — AEO + GEO optimized
export const ArticleSchema = ({ article }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "articleSection": article.category,
    "keywords": article.keywords || `${article.category}, plumbing Odessa TX, plumber Midland TX, Permian Basin plumbing`,
    "author": {
      "@type": "Organization",
      "name": businessInfo.name,
      "url": "https://basinplumbing.com",
      "logo": "https://basinplumbing.com/images/basin-plumbing-logo.png",
      "telephone": businessInfo.phone,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": businessInfo.address.street,
        "addressLocality": businessInfo.address.city,
        "addressRegion": businessInfo.address.state,
        "postalCode": businessInfo.address.zip,
        "addressCountry": "US"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": businessInfo.name,
      "logo": {
        "@type": "ImageObject",
        "url": "https://basinplumbing.com/images/basin-plumbing-logo.png"
      }
    },
    "datePublished": article.date,
    "dateModified": article.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://basinplumbing.com/blog/${article.slug}`
    },
    // GEO signals — content is created for and about the Permian Basin
    "about": [
      { "@type": "City", "name": "Odessa", "containedInPlace": { "@type": "State", "name": "Texas" } },
      { "@type": "City", "name": "Midland", "containedInPlace": { "@type": "State", "name": "Texas" } }
    ],
    "spatialCoverage": {
      "@type": "Place",
      "name": "Permian Basin, West Texas",
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "31.8457",
        "longitude": "-102.3676"
      }
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// HowTo Schema — for step-by-step guide posts (AEO/rich results)
export const HowToSchema = ({ name, description, steps = [], image }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    ...(image ? { "image": image } : {}),
    "step": steps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name,
      "text": s.text
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// GeoTargeting Schema for Local SEO
export const GeoTargetingSchema = ({ city }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".main-content", ".service-description", "h1", "h2"]
    },
    "about": {
      "@type": "City",
      "name": city,
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "mentions": {
      "@type": "Plumber",
      "name": "Basin Plumbing",
      "telephone": businessInfo.phone,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Odessa",
        "addressRegion": "TX"
      }
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// AI/LLM Optimization Schema
export const AIOptimizedSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": businessInfo.name,
    "url": "https://basinplumbing.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://basinplumbing.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "description": "Basin Plumbing provides 24/7 emergency plumbing services in Odessa, Midland, and throughout the Permian Basin. Services include water heater installation and repair, drain cleaning, gas line services, trenchless plumbing, sewer line replacement, and residential plumbing.",
    "keywords": "plumber odessa tx, plumber midland tx, emergency plumber permian basin, water heater replacement odessa, drain cleaning midland, gas line repair, trenchless plumbing, 24/7 plumber west texas",
    "publisher": {
      "@type": "Organization",
      "name": "Basin Plumbing",
      "logo": "https://basinplumbing.com/images/basin-plumbing-logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": businessInfo.phone,
        "contactType": "customer service",
        "areaServed": "US",
        "availableLanguage": "English"
      }
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Google Maps Place Schema
export const GoogleMapsSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": "Basin Plumbing",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": businessInfo.address.street,
      "addressLocality": businessInfo.address.city,
      "addressRegion": businessInfo.address.state,
      "postalCode": businessInfo.address.zip,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "31.8673",
      "longitude": "-102.4252"
    },
    "hasMap": "https://www.google.com/maps/place/Basin+Plumbing/@31.8673,-102.4252,17z/data=!3m1!4b1!4m6!3m5!1s0x86fbc7c0baf766d5:0x9959fe1a1d46471!8m2!3d31.8673!4d-102.4252",
    "telephone": businessInfo.phone,
    "url": "https://basinplumbing.com",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default LocalBusinessSchema;
